import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  OnDestroy,
  HostBinding,
  NgZone,
} from '@angular/core';
import { Browser } from '@capacitor/browser';
import jsQR from 'jsqr';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-qr-code-scanner',
  templateUrl: './qr-code-scanner.component.html',
  styleUrls: ['./qr-code-scanner.component.scss'],
})
export class QrCodeScannerComponent implements OnDestroy {
  @Output() scanComplete = new EventEmitter<string>();
  @Output() scanError = new EventEmitter<string>();
  @Output() scanCancel = new EventEmitter<void>();

  @HostBinding('class.scanner-active') isScanning = false;

  @ViewChild('video', { static: false }) video: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: false }) canvas: ElementRef<HTMLCanvasElement>;

  private internalUrls = [
    `https://${environment.webDomain}`,
    `https://${environment.premierWebDomain}`,
    `https://${environment.mobileDomain}`,
  ];

  private touchPrefix = '/touch';
  private desktopPrefix = '/desktop';
  private scanDelay = 1000;

  private stream: MediaStream | null = null;
  private animationFrameId: number | null = null;

  private isProcessingScan = false;

  constructor(
    private ngZone: NgZone,
    private router: Router,
  ) {}

  private shouldNotOpenScanner(): boolean {
    return (
      this.isScanning ||
      this.isProcessingScan ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    );
  }

  async openQrCodeScanner() {
    if (this.shouldNotOpenScanner()) {
      this.scanError.emit('i18n.qrCodeScanner.error.scannerAlreadyOpen');
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      this.isScanning = true;
      this.initializeVideoStream();
    } catch (err) {
      this.handleCameraAccessError(err);
    }
  }

  private isVideoStreamUninitialized(): boolean {
    return !this.video || !this.video.nativeElement || !this.stream;
  }

  private initializeVideoStream() {
    if (this.isVideoStreamUninitialized()) return;

    this.video.nativeElement.srcObject = this.stream;
    this.video.nativeElement.onloadedmetadata = () => {
      if (this.video && this.video.nativeElement) {
        this.video.nativeElement.play();
        // Running the scan loop outside of Angular's zone prevents unnecessary change detection cycles,
        // which can improve performance, especially with continuous operations like camera frame processing.
        // Angular's change detection will be re-entered when a QR code is found (using ngZone.run() in handleScanResult)
        // or when the scan is stopped (using ngZone.run() in stopScan).
        this.ngZone.runOutsideAngular(() => {
          this.scanLoop();
        });
      }
    };
    this.video.nativeElement.onerror = err => {
      this.scanError.emit('i18n.qrCodeScanner.error.videoPlaybackError');
      this.stopScan();
    };
  }

  private handleCameraAccessError(err: any) {
    let message = 'i18n.qrCodeScanner.error.cameraAccessError';
    if (err instanceof Error) {
      if (err.name === 'NotAllowedError') {
        message = 'i18n.qrCodeScanner.error.cameraPermissionDenied';
      } else if (err.name === 'NotFoundError') {
        message = 'i18n.qrCodeScanner.error.cameraNotFound';
      }
    }
    this.scanError.emit(message);
    this.isScanning = false;
  }

  private shouldWaitForVideoStream(): boolean {
    return (
      !this.isScanning ||
      !this.video.nativeElement ||
      this.video.nativeElement.readyState !==
        this.video.nativeElement.HAVE_ENOUGH_DATA
    );
  }

  private scanLoop() {
    if (this.shouldWaitForVideoStream()) {
      if (this.isScanning) {
        this.animationFrameId = requestAnimationFrame(this.scanLoop.bind(this));
      }
      return;
    }

    const videoEl = this.video.nativeElement;
    const canvasEl = this.canvas.nativeElement;
    this.processFrameAndDecode(videoEl, canvasEl);
  }

  private processFrameAndDecode(
    videoEl: HTMLVideoElement,
    canvasEl: HTMLCanvasElement,
  ) {
    const context = canvasEl.getContext('2d');

    if (context) {
      canvasEl.height = videoEl.videoHeight;
      canvasEl.width = videoEl.videoWidth;
      context.drawImage(videoEl, 0, 0, canvasEl.width, canvasEl.height);
      const imageData = context.getImageData(
        0,
        0,
        canvasEl.width,
        canvasEl.height,
      );

      try {
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });

        if (code) {
          this.ngZone.run(() => {
            this.handleScanResult(code.data);
          });
          return;
        }
      } catch (err) {
        console.error('jsQR error:', err);
      }
    }

    this.animationFrameId = requestAnimationFrame(this.scanLoop.bind(this));
  }

  private isValidUrl(scannedText: string): boolean {
    return (
      scannedText.startsWith('http://') || scannedText.startsWith('https://')
    );
  }

  private handleScanResult(scannedText: string) {
    if (this.isProcessingScan) return;

    this.isProcessingScan = true;

    setTimeout(() => {
      this.stopScan();
      if (this.isValidUrl(scannedText)) {
        this.navigateToScannedUrl(scannedText);
      } else {
        this.scanError.emit('i18n.qrCodeScanner.error.invalidUrl');
      }
      this.isProcessingScan = false;
    }, this.scanDelay);
  }

  private navigateToScannedUrl(scannedText: string) {
    this.ngZone.run(() => {
      if (this.internalUrls.some(url => scannedText.startsWith(url))) {
        this.navigateToInternalUrl(scannedText);
      } else {
        Browser.open({ url: scannedText }).catch(err => {
          this.scanError.emit('i18n.qrCodeScanner.error.failedToOpenUrl');
        });
      }
    });
  }

  private navigateToInternalUrl(scannedText: string) {
    let url = scannedText;

    const prefix = this.internalUrls.find(url => scannedText.startsWith(url));

    url = scannedText.substring(prefix.length);
    url = url.replace(this.desktopPrefix, this.touchPrefix);

    if (url && !url.startsWith('/')) {
      url = `/${url}`;
    }

    this.router
      .navigateByUrl(url)
      .then(navigated => {
        if (navigated) {
          this.scanComplete.emit(scannedText);
        } else {
          // Fallback to Browser.open if router.navigateByUrl fails
          Browser.open({ url: scannedText }).catch(err => {
            this.scanError.emit('i18n.qrCodeScanner.error.failedToOpenUrl');
          });
        }
      })
      .catch(err => {
        this.scanError.emit('i18n.qrCodeScanner.error.failedToOpenUrl');
      });
  }

  stopScan() {
    this.ngZone.run(() => {
      if (!this.isScanning) return; // Avoid running multiple times

      this.isScanning = false;

      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }

      if (this.video && this.video.nativeElement) {
        this.video.nativeElement.srcObject = null;
        this.video.nativeElement.onloadedmetadata = null;
        this.video.nativeElement.onerror = null;
      }

      this.scanCancel.emit();
    });
  }

  ngOnDestroy() {
    this.stopScan();
  }
}
